import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard, faFileExcel } from '@fortawesome/free-regular-svg-icons';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import {
  setRectificationsByProductData,
  setRectificationsByProductFilters,
  setReportIsVisible,
} from '../../../../../store/slices/reportsSlice';
import {
  DateRangeTableFilterType, initPaging, PickerValues,
} from '../../../../../core/types/coreTypes';
import KeywordFiltersSection from '../../../../../core/components/keyword-filters-section/KeywordFiltersSection';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import {
  rectificationsByProductFiltersDateSelector, rectificationsByProductFiltersSelector,
  rectificationsByProductGlobalFiltersSelector, rectificationsByProductTreeSelector,
} from '../../../../../store/selectors/reportsSelectors';
import Button from '../../../../../core/components/button/Button';
import {
  getRectificationsByProductReportExcelThunk,
  getRectificationsByProductReportThunk,
} from '../../../../../store/thunks/reports/reportsThunks';
import Dropdown from '../../../../../core/components/dropdown/Dropdown';
import { useRectificationsByProductReportDefaultPaging } from '../../../../hooks/useRectificationsByProductReportDefaultPaging';
import { getCustomersDictionaryThunk } from '../../../../../store/thunks/shared/sharedThunks';
import { getRectificationFaultTypesThunk } from '../../../../../store/thunks/coreThunk';
import { initRectificationsByProductReportData } from '../../../reports-content/sales/rectifications-by-product/utils/data';
import { RectificationsByProductReportFilters } from '../../../../types/RectificationsByProductTypes';

const RectificationsByProductGlobalFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const checkboxFilters = useAppSelector(rectificationsByProductGlobalFiltersSelector);
  const treeFilters = useAppSelector(rectificationsByProductTreeSelector);
  const datePickerFilters = useAppSelector(rectificationsByProductFiltersDateSelector);
  const filters = useAppSelector(rectificationsByProductFiltersSelector);
  const globalFiltersNames = [...checkboxFilters, ...datePickerFilters].map((el) => el.name as keyof RectificationsByProductReportFilters);
  const isSelectedGlobalFilters = globalFiltersNames.some((field) => filters[field]);

  const [isExpanded, setIsExpanded] = useState(false);

  const defaultPagingAndOrder = useRectificationsByProductReportDefaultPaging();

  useEffect(() => {
    dispatch(getCustomersDictionaryThunk({ filters: undefined }));
    dispatch(getRectificationFaultTypesThunk());

    return () => {
      dispatch(setRectificationsByProductFilters({ ...initPaging, ...defaultPagingAndOrder }));
      dispatch(setRectificationsByProductData(initRectificationsByProductReportData));
    };
    // eslint-disable-next-line
  }, []);

  const initialPickerValue: DateRangeTableFilterType = {
    completedDate: [null, null],
  };
  const [pickerValue, setPickerValue] = useState<DateRangeTableFilterType>(initialPickerValue);

  const pickerHandle = (field: string, values?: PickerValues) => {
    const newFilters: RectificationsByProductReportFilters = {
      ...filters,
      page: 1,
      [field]: values || undefined,
    };
    dispatch(setRectificationsByProductFilters(newFilters));
    dispatch(setRectificationsByProductData(initRectificationsByProductReportData));
    dispatch(setReportIsVisible(false));
  };

  const clearFilters = () => {
    setPickerValue(initialPickerValue);
    dispatch(setRectificationsByProductFilters({
      ...defaultPagingAndOrder,
      page: 1,
      pageSize: filters.pageSize,
    }));
    dispatch(setRectificationsByProductData(initRectificationsByProductReportData));
    dispatch(setReportIsVisible(false));
  };

  const getFilteredResHandle = (values: number[], field: string) => {
    dispatch(setRectificationsByProductFilters({
      ...filters,
      page: 1,
      usedInRectifications: undefined,
      [field]: values,
    }));
    dispatch(setRectificationsByProductData(initRectificationsByProductReportData));
    dispatch(setReportIsVisible(false));
  };

  const treeChangeHandle = (checkedKeys: React.Key[]) => {
    const checkedSubcategories = checkedKeys.filter((el) => !el.toString().includes('category'));
    const values = checkedSubcategories.map((s) => +s);
    getFilteredResHandle(values, 'subcategories');
  };

  const exportToExcel = () => {
    const { order } = defaultPagingAndOrder;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      page, pageSize, partCode, partName, usedInRectifications, ...rest
    } = filters;
    dispatch(getRectificationsByProductReportExcelThunk({
      filters: {
        ...rest,
        order,
      },
    }));
  };

  return (
    <>
      <KeywordFiltersSection
        isSearch={false}
        filters={checkboxFilters}
        treeFilters={treeFilters}
        treeChangeHandle={treeChangeHandle}
        dateFilters={datePickerFilters}
        pickerValue={pickerValue}
        setPickerValue={setPickerValue}
        pickerHandle={pickerHandle}
        isFullWidth
        onClear={clearFilters}
        className={classNames('reportFilters__filters', { 'reportFilters__filters--expanded': isExpanded })}
        getFilteredResHandle={getFilteredResHandle}
      />
      <div className="reportFilters__actions">
        <Button
          label={`${isExpanded ? 'Hide' : 'Show'} filters`}
          onClick={() => setIsExpanded(!isExpanded)}
          className={classNames('reportFilters__expand-btn', { 'reportFilters__expand-btn--active': isSelectedGlobalFilters })}
        />
        <Button
          label="Show report"
          className="reportFilters__action-button"
          icon={<FontAwesomeIcon icon={faClipboard} />}
          onClick={() => {
            dispatch(getRectificationsByProductReportThunk({
              filters,
            }));
            dispatch(setReportIsVisible(true));
          }}
        />
        <Dropdown
          className="reportFilters__action-dropdown"
          icon={<FontAwesomeIcon icon={faChevronDown} />}
          menuItems={[{
            label: 'Export to Excel',
            key: 'export-to-excel',
            icon: <FontAwesomeIcon icon={faFileExcel} />,
            onClick: exportToExcel,
          }]}
          placement="bottomRight"
          parentRender
        />
      </div>
    </>
  );
};

export default RectificationsByProductGlobalFilters;
